/**
 * TanStack Query hooks for Media
 *
 * This file provides React Query hooks for fetching and mutating media data.
 * These hooks replace the async methods in the Zustand media store.
 *
 * Cross-store synchronization:
 * - Media is also populated by products and bulk-groups queries via setMediasInCache()
 *
 * @module media-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type Media, type MediaKind } from '../../entity/api/media';
import {
  type CreateMediaWithFileParams,
  createMediaWithFile,
  getMediaByRefDocId,
  getMediasList,
} from '../../sdk/media';

// ============ QUERY KEYS ============

export const mediaKeys = {
  all: ['media'] as const,
  list: function () {
    return [...mediaKeys.all, 'list'] as const;
  },
  byId: function (id: string) {
    return [...mediaKeys.all, id] as const;
  },
  byRefDocId: function (refDocId: string) {
    return [...mediaKeys.all, 'refDoc', refDocId] as const;
  },
};

// ============ QUERIES ============

/**
 * Fetch all media list
 *
 * Replaces: getStores().media.getState().list()
 */
export function useMediaList() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: mediaKeys.list(),
    queryFn: async function () {
      const response = await getMediasList();
      const medias = response.data;

      // Cache each media individually
      for (const media of medias) {
        queryClient.setQueryData(mediaKeys.byId(media._id), media);
      }

      // Also group and cache by refDocId
      const byRefDocId: Record<string, Media[]> = {};
      for (const media of medias) {
        if (media.refDocId != null) {
          if (byRefDocId[media.refDocId] == null) {
            byRefDocId[media.refDocId] = [];
          }
          byRefDocId[media.refDocId].push(media);
        }
      }

      for (const [refDocId, refMedias] of Object.entries(byRefDocId)) {
        queryClient.setQueryData(mediaKeys.byRefDocId(refDocId), refMedias);
      }

      return medias;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch media by reference document ID
 *
 * Replaces: getStores().media.getState().getByRefDocId(refDocId)
 */
export function useMediaByRefDocId(refDocId: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: mediaKeys.byRefDocId(refDocId ?? ''),
    queryFn: async function () {
      if (refDocId == null) {
        throw new Error('RefDocId is required');
      }
      const medias = await getMediaByRefDocId(refDocId);

      // Cache each media individually
      for (const media of medias) {
        queryClient.setQueryData(mediaKeys.byId(media._id), media);
      }

      return medias;
    },
    enabled: refDocId != null && refDocId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============ MUTATIONS ============

/**
 * Create new media
 * Note: The API returns an array of created media items
 *
 * Replaces: createMedia SDK call (no direct Zustand equivalent)
 */
export function useCreateMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: CreateMediaWithFileParams) {
      const medias = await createMediaWithFile({
        file: data.file,
        refDocId: data.refDocId,
        organizationId: data.organizationId,
        kind: data.kind,
      });
      return medias;
    },
    onSuccess: function (medias) {
      // Cache each new media item
      for (const media of medias) {
        queryClient.setQueryData(mediaKeys.byId(media._id), media);
      }

      // Group by refDocId and update caches
      const byRefDocId: Record<string, Media[]> = {};
      for (const media of medias) {
        if (media.refDocId != null) {
          if (byRefDocId[media.refDocId] == null) {
            byRefDocId[media.refDocId] = [];
          }
          byRefDocId[media.refDocId].push(media);
        }
      }

      for (const [refDocId, newMedias] of Object.entries(byRefDocId)) {
        queryClient.setQueryData<Media[]>(mediaKeys.byRefDocId(refDocId), function (oldList) {
          if (oldList == null) {
            return newMedias;
          }
          return [...oldList, ...newMedias];
        });
      }

      // Invalidate list to include the new media
      void queryClient.invalidateQueries({ queryKey: mediaKeys.list() });
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Set multiple media items in cache
 * Used by products and bulk-groups queries for cross-store sync
 */
export function setMediasInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  medias: Media[]
): void {
  // Cache each media individually
  for (const media of medias) {
    queryClient.setQueryData(mediaKeys.byId(media._id), media);
  }

  // Also update the list cache if it exists
  const existingList = queryClient.getQueryData<Media[]>(mediaKeys.list());
  if (existingList != null) {
    // Create a map for quick lookup
    const mediaMap = new Map<string, Media>();
    for (const m of existingList) {
      mediaMap.set(m._id, m);
    }

    // Add or update media items
    for (const media of medias) {
      mediaMap.set(media._id, media);
    }

    queryClient.setQueryData(mediaKeys.list(), Array.from(mediaMap.values()));
  }

  // Group media by refDocId and update those caches
  const byRefDocId: Record<string, Media[]> = {};
  for (const media of medias) {
    if (media.refDocId != null) {
      if (byRefDocId[media.refDocId] == null) {
        byRefDocId[media.refDocId] = [];
      }
      byRefDocId[media.refDocId].push(media);
    }
  }

  for (const [refDocId, refMedias] of Object.entries(byRefDocId)) {
    const existingRefMedia = queryClient.getQueryData<Media[]>(mediaKeys.byRefDocId(refDocId));
    if (existingRefMedia != null) {
      const refMediaMap = new Map<string, Media>();
      for (const m of existingRefMedia) {
        refMediaMap.set(m._id, m);
      }
      for (const media of refMedias) {
        refMediaMap.set(media._id, media);
      }
      queryClient.setQueryData(mediaKeys.byRefDocId(refDocId), Array.from(refMediaMap.values()));
    } else {
      queryClient.setQueryData(mediaKeys.byRefDocId(refDocId), refMedias);
    }
  }
}

/**
 * Set a single media item in cache
 */
export function setMediaInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  media: Media
): void {
  queryClient.setQueryData(mediaKeys.byId(media._id), media);

  // Also update refDocId cache
  queryClient.setQueryData<Media[]>(mediaKeys.byRefDocId(media.refDocId), function (oldList) {
    if (oldList == null) {
      return [media];
    }
    // Check if media already exists
    const exists = oldList.some(function (m) {
      return m._id === media._id;
    });
    if (exists) {
      return oldList.map(function (m) {
        if (m._id === media._id) {
          return media;
        }
        return m;
      });
    }
    return [...oldList, media];
  });
}

/**
 * Get media by ID from cache
 */
export function getMediaFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Media | undefined {
  return queryClient.getQueryData<Media>(mediaKeys.byId(id));
}

/**
 * Get media list from cache
 */
export function getMediaListFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): Media[] | undefined {
  return queryClient.getQueryData<Media[]>(mediaKeys.list());
}

/**
 * Get media by refDocId from cache
 */
export function getMediaByRefDocIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  refDocId: string
): Media[] {
  return queryClient.getQueryData<Media[]>(mediaKeys.byRefDocId(refDocId)) ?? [];
}

/**
 * Get media by refDocId and kind from cache (client-side filter)
 * Replaces: getStores().media.getState().getMediaFromStore({ kind, refDocId })
 */
export function getMediaByRefDocIdAndKindFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  refDocId: string,
  kind: MediaKind
): Media[] {
  const medias = getMediaByRefDocIdFromCache(queryClient, refDocId);
  return medias.filter(function (m) {
    return m.kind === kind;
  });
}

/**
 * Remove media from cache by ID
 */
export function removeMediaFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): void {
  const media = queryClient.getQueryData<Media>(mediaKeys.byId(id));

  // Remove from individual cache
  queryClient.removeQueries({ queryKey: mediaKeys.byId(id) });

  // Remove from refDocId cache if exists
  if (media != null) {
    queryClient.setQueryData<Media[]>(mediaKeys.byRefDocId(media.refDocId), function (oldList) {
      if (oldList == null) {
        return oldList;
      }
      return oldList.filter(function (m) {
        return m._id !== id;
      });
    });
  }

  // Remove from list cache
  queryClient.setQueryData<Media[]>(mediaKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.filter(function (m) {
      return m._id !== id;
    });
  });
}

/**
 * Invalidate all media queries
 */
export function invalidateMedia(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: mediaKeys.all });
}

/**
 * Invalidate media by refDocId
 */
export function invalidateMediaByRefDocId(
  queryClient: ReturnType<typeof useQueryClient>,
  refDocId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: mediaKeys.byRefDocId(refDocId),
  });
}

// ============ IMPERATIVE FUNCTIONS ============

/**
 * Fetch media by refDocId and cache them (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand store for backward compatibility.
 *
 * Replaces: getStores().mediaStore.getState().getByRefDocId(refDocId)
 */
export async function fetchMediaByRefDocIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  refDocId: string
): Promise<Media[]> {
  const medias = await getMediaByRefDocId(refDocId);

  // Cache each media individually
  for (const media of medias) {
    queryClient.setQueryData(mediaKeys.byId(media._id), media);
  }

  // Cache by refDocId
  queryClient.setQueryData(mediaKeys.byRefDocId(refDocId), medias);

  return medias;
}

/**
 * Fetch all media list and cache them (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand store for backward compatibility.
 *
 * Replaces: getStores().mediaStore.getState().list()
 */
export async function fetchMediaListImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<Media[]> {
  const response = await getMediasList();
  const medias = response.data;

  // Cache each media individually
  for (const media of medias) {
    queryClient.setQueryData(mediaKeys.byId(media._id), media);
  }

  // Group and cache by refDocId
  const byRefDocId: Record<string, Media[]> = {};
  for (const media of medias) {
    if (media.refDocId != null) {
      if (byRefDocId[media.refDocId] == null) {
        byRefDocId[media.refDocId] = [];
      }
      byRefDocId[media.refDocId].push(media);
    }
  }

  for (const [refDocId, refMedias] of Object.entries(byRefDocId)) {
    queryClient.setQueryData(mediaKeys.byRefDocId(refDocId), refMedias);
  }

  // Cache the full list
  queryClient.setQueryData(mediaKeys.list(), medias);

  return medias;
}
